// apps/web/src/features/crm/deals/queries.ts — TanStack Query hooks for the
// CRM Deals surface.
//
// #2248 retired every Deal WRITE hook (create/update/stage/archive/restore) and
// the per-stage analytics reader: the `/deals` product list now reads the v4
// Engagement projection (`useEngagementsList`, bucket=DEAL), and no UI mutates a
// Deal. What survives is the READ path the frozen request→deal chip still needs
// (D-B1): `useDeal` resolves a legacy `GET /deals/:id` for the `?openId=`
// drawer, and `dealsKeys` still keys the linked-request rows (`linkedRequests`).
import { useQuery } from '@tanstack/react-query';
import { DealListItemSchema, type DealListItem } from '@magimp/shared';
import { useApiClient } from '@magimp/shared-hooks';

// WHY: the deal detail + its linked-request rows share the `detail` prefix so a
//   prefix invalidation of a deal flushes both. Kept minimal after the write
//   hooks (and their list/analytics keys) were removed with the Deal write UI.
export const dealsKeys = {
  // WHY (#1598): the `with-count` suffix is appended ONLY when the detail
  //   surface opts into `?includeRequestCount=true`, so the DEFAULT
  //   `detail(id)` key stays byte-for-byte unchanged. Prefix-based
  //   `invalidateQueries(['deals','detail',id])` still matches both variants.
  detail: (id: string, opts?: { includeRequestCount?: boolean }) =>
    opts?.includeRequestCount
      ? (['deals', 'detail', id, 'with-count'] as const)
      : (['deals', 'detail', id] as const),
  // WHY (#1598): keyed under the deal's detail prefix so a prefix
  //   invalidation of the deal flushes its linked-request rows too.
  requests: (id: string) => ['deals', 'detail', id, 'requests'] as const,
};

/**
 * @aiPurpose `GET /deals/:id`. `options.includeRequestCount` (#1598) is
 *   OPT-IN: only the detail surface that renders the linked-requests
 *   section requests `?includeRequestCount=true` and gets its own cache
 *   entry. Default `useDeal(id)` reads stay shape-compatible (no
 *   `requestCount`) on the unchanged default key. The endpoint stays alive
 *   (PB-C keeps GET reads) so the frozen request→deal `?openId=` chip
 *   resolves against the legacy deal (D-B1).
 */
export function useDeal(id: string | null, options?: { includeRequestCount?: boolean }) {
  const client = useApiClient();
  const includeRequestCount = options?.includeRequestCount === true;
  return useQuery<DealListItem>({
    queryKey: dealsKeys.detail(id ?? '', { includeRequestCount }),
    enabled: !!id,
    queryFn: ({ signal }) =>
      client.get<DealListItem>(
        `/deals/${id}`,
        includeRequestCount ? { signal, query: { includeRequestCount: true } } : { signal },
        DealListItemSchema,
      ),
  });
}
